// Note. 의도치 않은 예외 상황에 대한 로그를 Sentry로 수집하는 모듈입니다.

import {getUserLoginId} from '@/utils/main';
import {
  SENTRY_IGNORE_ERROR_LIST,
  SoopSentryHub,
} from '../../../../sentry.client.config';
import * as Sentry from '@sentry/browser';

class Logger {
  /**
   * 로거 인스턴스 생성 시 사용자 정보 등록
   */
  constructor() {
    const userId = getUserLoginId();

    if (!!userId) {
      this.setUser(userId);
    } else {
      this.clearUser();
    }
  }

  /**
   * @param {Sentry.SeverityLevel} level
   * @param {string | Error} message
   * @param {Object} [extraData] - 로그에 포함할 추가 정보
   */
  #log(level, message, extraData = {}) {
    if (
      /**
       * Note.
       * `sentry.client.config.js` 파일에서 `ignoreErrors` 설정이 가능하나
       * 일부 메시지에 적용이 되지 않는 문제가 있어 어플리케이션 내부에서 추가 설정
       */
      SENTRY_IGNORE_ERROR_LIST.includes(
        message instanceof Error ? message.message : message,
      )
    ) {
      return;
    }

    SoopSentryHub.withScope((scope) => {
      try {
        scope.setExtras({...extraData, referrer: document.referrer});
        if (extraData.errorType) {
          scope.setTag('errorType', extraData.errorType);
        }
        scope.setLevel(level);

        if (level === 'error' || level === 'fatal') {
          SoopSentryHub.captureException(
            message instanceof Error ? message : new Error(message),
          );
        } else {
          SoopSentryHub.captureMessage(message);
        }
      } catch (e) {
        // Note: 로그 발송 중 에러 상황에 대한 방어
      }
    });
  }

  /**
   * @param {string | Error} message
   * @param {Object} [extraData] - 로그에 포함할 추가 정보
   */
  info(message, extraData) {
    this.#log('info', message, extraData);
  }
  /**
   * @param {string | Error} message
   * @param {Object} [extraData] - 로그에 포함할 추가 정보
   */
  warn(message, extraData) {
    this.#log('warning', message, extraData);
  }
  /**
   * @param {string | Error} message
   * @param {Object} [extraData] - 로그에 포함할 추가 정보
   */
  error(message, extraData) {
    this.#log('error', message, extraData);
  }

  /**
   * @param {string | Error} message
   * @param {Object} [extraData] - 로그에 포함할 추가 정보
   */
  fatal(message, extraData) {
    this.#log('fatal', message, extraData);
  }

  /**
   * @param {string | Error} message
   * @param {Object} [extraData] - 로그에 포함할 추가 정보
   */
  debug(message, extraData) {
    this.#log('debug', message, extraData);
  }

  setUser(userId) {
    SoopSentryHub.setUser({id: userId});
  }

  clearUser() {
    SoopSentryHub.setUser(null);
  }
}

/**
 * Sentry 적용 공통 로거 모듈 입니다.
 */
export const logger = new Logger();
